import { useState, useEffect } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import { adminApi, tenantsApi, activityApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import './Dashboard.css';

const Dashboard = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([
    { label: 'Total Tenants', value: '-', change: '-', changeType: 'neutral' },
    { label: 'Active Users', value: '-', change: '-', changeType: 'neutral' },
    { label: 'MRR', value: '-', change: '-', changeType: 'neutral' },
    { label: 'Fraud Blocked', value: '-', change: '-', changeType: 'neutral' },
  ]);
  const [activity, setActivity] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel
      const [dashboardData, tenantsData, activityData] = await Promise.allSettled([
        adminApi.dashboard(),
        tenantsApi.list({ limit: 3, sort: 'users_count:desc' }),
        activityApi.list({ limit: 5 }),
      ]);

      // Process dashboard stats
      if (dashboardData.status === 'fulfilled' && dashboardData.value) {
        const data = dashboardData.value;
        setStats([
          {
            label: 'Total Tenants',
            value: formatNumber(data.totalTenants || data.companies || 0),
            change: formatPercent(data.tenantsGrowth),
            changeType: getChangeType(data.tenantsGrowth),
          },
          {
            label: 'Active Users',
            value: formatNumber(data.activeUsers || data.users || 0),
            change: formatPercent(data.usersGrowth),
            changeType: getChangeType(data.usersGrowth),
          },
          {
            label: 'MRR',
            value: formatCurrency(data.mrr || data.revenue || 0),
            change: formatPercent(data.mrrGrowth || data.revenueGrowth),
            changeType: getChangeType(data.mrrGrowth || data.revenueGrowth),
          },
          {
            label: 'Fraud Blocked',
            value: formatNumber(data.fraudBlocked || data.securityEvents || 0),
            change: formatPercent(data.fraudGrowth),
            changeType: data.fraudGrowth > 0 ? 'warning' : 'positive',
          },
        ]);
      }

      // Process tenants
      if (tenantsData.status === 'fulfilled') {
        const data = tenantsData.value;
        setTenants(data.companies || data.data || data.items || []);
      }

      // Process activity
      if (activityData.status === 'fulfilled') {
        const data = activityData.value;
        setActivity(data.logs || data.data || data.items || []);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message);
      showToast('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}K`;
    return num.toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return `${Math.abs(value).toFixed(1)}%`;
  };

  const getChangeType = (value) => {
    if (value === null || value === undefined) return 'neutral';
    return value >= 0 ? 'positive' : 'negative';
  };

  const getActivityIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'login':
      case 'user_login':
        return { icon: 'user', className: 'info' };
      case 'fraud':
      case 'security':
      case 'blocked':
        return { icon: 'alert-triangle', className: 'warning' };
      case 'tenant':
      case 'company_created':
        return { icon: 'check-circle', className: 'success' };
      case 'upgrade':
      case 'subscription':
        return { icon: 'credit-card', className: 'info' };
      default:
        return { icon: 'activity', className: 'info' };
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const seconds = Math.floor((Date.now() - new Date(timestamp)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Welcome back! Here's what's happening.</p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            icon="refresh"
            onClick={fetchDashboardData}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
          <Button variant="primary" icon="plus">New Tenant</Button>
        </div>
      </div>

      {error && (
        <div className="dashboard-error">
          <span className="icon"><svg><use href="#icon-alert-triangle"></use></svg></span>
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={fetchDashboardData}>Retry</Button>
        </div>
      )}

      <div className="stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loading} />
        ))}
      </div>

      <div className="dashboard-grid">
        <section className="section">
          <div className="section-header">
            <h2 className="section-title">Recent Activity</h2>
            <Button variant="ghost" size="sm">View All</Button>
          </div>
          <div className="activity-list">
            {loading ? (
              <div className="activity-loading">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="activity-item skeleton">
                    <div className="skeleton-icon"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-line"></div>
                      <div className="skeleton-line short"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : activity.length > 0 ? (
              activity.map((item, index) => {
                const { icon, className } = getActivityIcon(item.action || item.type);
                return (
                  <div key={item.id || index} className="activity-item">
                    <div className={`activity-icon ${className}`}>
                      <span className="icon"><svg><use href={`#icon-${icon}`}></use></svg></span>
                    </div>
                    <div className="activity-content">
                      <div className="activity-title">{item.description || item.action || item.message}</div>
                      <div className="activity-meta">
                        {item.user?.email || item.actor || item.ip || 'System'} - {formatTimeAgo(item.createdAt || item.timestamp)}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="activity-empty">
                <span className="icon"><svg><use href="#icon-activity"></use></svg></span>
                <p>No recent activity</p>
              </div>
            )}
          </div>
        </section>

        <section className="section">
          <div className="section-header">
            <h2 className="section-title">Top Tenants</h2>
            <Button variant="ghost" size="sm">View All</Button>
          </div>
          <div className="tenant-list">
            {loading ? (
              <div className="tenant-loading">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="tenant-row skeleton">
                    <div className="skeleton-content">
                      <div className="skeleton-line"></div>
                      <div className="skeleton-line short"></div>
                    </div>
                    <div className="skeleton-badge"></div>
                  </div>
                ))}
              </div>
            ) : tenants.length > 0 ? (
              tenants.map((tenant) => (
                <div key={tenant.id} className="tenant-row">
                  <div className="tenant-info">
                    <div className="tenant-name">{tenant.name}</div>
                    <div className="tenant-domain">{tenant.domain || tenant.subdomain || '-'}</div>
                  </div>
                  <div className="tenant-stats">
                    <span className="tenant-users">{formatNumber(tenant.usersCount || tenant.users_count || 0)} users</span>
                    <span className={`tenant-badge ${tenant.status === 'active' ? 'active' : 'inactive'}`}>
                      {tenant.status || 'Active'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="tenant-empty">
                <span className="icon"><svg><use href="#icon-building"></use></svg></span>
                <p>No tenants yet</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
